import { useState, useEffect, useMemo, useRef } from 'react';
import DriveWorkspaceStore from '../../../stores/DriveWorkspace/DriveWorkspaceStore';
import { SelectionStore } from '../../../stores/Selection/SelectionStore';

/**
 * Hook for managing DriveWorkspace data loading and synchronization
 * Handles store creation, drive loading, workspace data loading, and state sync
 * 
 * @param {Object} options - Hook options
 * @param {string} options.finalVaultUuid - The vault UUID for the current drive
 * @param {boolean} options.isViewingFolder - Whether viewing a nested folder (not drive root)
 * @param {Object} options.drivesViewStore - The drives view store
 * @param {Object} options.storesRef - Ref containing singleton stores (foldersStore, documentsStore, etc.)
 * @returns {Object} - Store instance, state, and setters
 */
export const useDriveWorkspaceData = ({
  finalVaultUuid,
  isViewingFolder,
  drivesViewStore,
  storesRef,
}) => {
  // Local state for data
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);

  // Refs for tracking loading state
  const isLoadingWorkspace = useRef(false);
  const drivesLoadedRef = useRef(false);
  const lastLoadedVaultUuidRef = useRef(null);
  const lastViewingFolderRef = useRef(null);

  // Create store instance with vaultUuid - only recreate when vaultUuid changes
  const driveWorkspaceStore = useMemo(() => {
    const selectionStore = new SelectionStore();
    return new DriveWorkspaceStore({
      foldersStore: storesRef.current.foldersStore,
      documentsStore: storesRef.current.documentsStore,
      agentsStore: storesRef.current.agentsStore,
      resourcesStore: storesRef.current.resourcesStore,
      usersStore: storesRef.current.usersStore,
      selectionStore,
      vaultUuid: finalVaultUuid
    });
  }, [finalVaultUuid, storesRef]);

  // Store reference in ref - update immediately, not in useEffect
  const storeRef = useRef(driveWorkspaceStore);
  storeRef.current = driveWorkspaceStore;

  // Extract primitive values from store to avoid store dependency
  const folderListLength = driveWorkspaceStore.folderList?.length ?? 0;
  const documentListLength = driveWorkspaceStore.documentList?.length ?? 0;

  // Load drives if not loaded yet (needed when accessing directly by URL)
  useEffect(() => {
    const loadDrivesIfNeeded = async () => {
      if (finalVaultUuid && !drivesLoadedRef.current && drivesViewStore?.loadDrivesData) {
        const drivesCount = drivesViewStore?.drivesStore?.drives?.length ?? 0;
        const driveExists = drivesViewStore?.drivesStore?.drives?.some(d => d.id === finalVaultUuid);
        
        if (drivesCount === 0 || !driveExists) {
          try {
            await drivesViewStore.loadDrivesData();
            drivesLoadedRef.current = true;
          } catch (error) {
            console.error('Error loading drives:', error);
            drivesLoadedRef.current = true;
          }
        } else {
          drivesLoadedRef.current = true;
        }
      }
    };
    loadDrivesIfNeeded();
  }, [finalVaultUuid, drivesViewStore]);

  // Load data when at drive root (not viewing a folder)
  useEffect(() => {
    const loadData = async () => {
      const currentStore = storeRef.current;
      
      // Check if vaultUuid changed - reset refs and clear state
      if (lastLoadedVaultUuidRef.current !== null && lastLoadedVaultUuidRef.current !== finalVaultUuid) {
        isLoadingWorkspace.current = false;
        lastLoadedVaultUuidRef.current = null;
        lastViewingFolderRef.current = null;
        setFolders([]);
        setDocuments([]);
        setIsDataLoading(true);
      }
      
      // Only load drive workspace data if NOT viewing a nested folder
      const vaultUuidChanged = lastLoadedVaultUuidRef.current !== finalVaultUuid;
      const viewModeChanged = lastViewingFolderRef.current !== isViewingFolder;
      const shouldLoad = !isViewingFolder && 
                        finalVaultUuid && 
                        !isLoadingWorkspace.current &&
                        (vaultUuidChanged || viewModeChanged);
      
      if (shouldLoad) {
        isLoadingWorkspace.current = true;
        setIsDataLoading(true);
        try {
          await currentStore.loadWorkspaceData();
          setFolders(currentStore.folderList || []);
          setDocuments(currentStore.documentList || []);
          lastLoadedVaultUuidRef.current = finalVaultUuid;
          lastViewingFolderRef.current = isViewingFolder;
        } finally {
          isLoadingWorkspace.current = false;
          setIsDataLoading(false);
        }
      } else if (!isViewingFolder && finalVaultUuid) {
        setIsDataLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalVaultUuid, isViewingFolder]);

  // Sync local state when store data changes
  useEffect(() => {
    if (!isViewingFolder && (folderListLength > 0 || documentListLength > 0)) {
      const currentStore = storeRef.current;
      setFolders(currentStore.folderList || []);
      setDocuments(currentStore.documentList || []);
    }
  }, [folderListLength, documentListLength, isViewingFolder]);

  return {
    // Store
    driveWorkspaceStore,
    storeRef,
    
    // Data state
    folders,
    documents,
    isDataLoading,
    
    // Setters (needed for upload complete handler)
    setFolders,
    setDocuments,
    
    // Derived values for convenience
    folderListLength,
    documentListLength,
  };
};

export default useDriveWorkspaceData;
